/**
 * Classifies a stock row's "last synced" state for the inventory grid's
 * synced-time cell (PRD v1.8 §4.6.3.2-C / A.7).
 *
 * The `/v1/stock` list endpoint carries `last_synced_at` and `updated_at`
 * but not the last push's result — that lives in the per-row push log
 * (`GET /v1/stock/{sku}/{mkp}/push-log`), which the cell doesn't fetch for
 * every row (would be N+1). So:
 *  - `failed` / `no_listing` are only derivable when the caller already has
 *    the row's last log entry (e.g. after opening the Push history drawer,
 *    or a future BE `last_push_result` column — not in scope, see PR notes).
 *  - Absent a log entry, the cell falls back to comparing `updated_at` vs
 *    `last_synced_at` to flag a push that's overdue (`pending`).
 */
export type SyncedState =
  | { kind: "never" }
  | { kind: "ok"; rel: string }
  | { kind: "pending"; rel: string }
  | { kind: "failed"; rel: string; error: string }
  | { kind: "no_listing" };

interface StockRowLike {
  last_synced_at?: string | null;
  updated_at?: string | null;
  // From the list endpoint (newest stock_push_log row) — lets the grid show
  // failed / no_listing without an N+1 push-log fetch (review I9).
  last_push_result?: string;
  last_push_error?: string;
}

interface PushLogEntryLike {
  at: string;
  result: string;
  message?: string;
}

/** A row is "overdue" once it's gone this long without picking up a newer update. */
const PENDING_THRESHOLD_MS = 60 * 60 * 1000;

export function lastSyncedState(
  row: StockRowLike,
  lastLog?: PushLogEntryLike,
  now: number = Date.now(),
): SyncedState {
  if (!lastLog && row.last_push_result) {
    lastLog = {
      at: row.last_synced_at ?? row.updated_at ?? "",
      result: row.last_push_result,
      ...(row.last_push_error ? { message: row.last_push_error } : {}),
    };
  }
  if (lastLog?.result === "no_listing") return { kind: "no_listing" };

  const syncedAtMs = row.last_synced_at ? new Date(row.last_synced_at).getTime() : undefined;

  if (
    lastLog?.result === "failed" &&
    (syncedAtMs === undefined || new Date(lastLog.at).getTime() > syncedAtMs)
  ) {
    return { kind: "failed", rel: lastLog.at, error: lastLog.message ?? "" };
  }

  if (!row.last_synced_at) return { kind: "never" };

  const updatedAtMs = row.updated_at ? new Date(row.updated_at).getTime() : undefined;
  if (
    updatedAtMs !== undefined &&
    syncedAtMs !== undefined &&
    updatedAtMs > syncedAtMs &&
    now - updatedAtMs > PENDING_THRESHOLD_MS
  ) {
    return { kind: "pending", rel: row.last_synced_at };
  }

  return { kind: "ok", rel: row.last_synced_at };
}
